// src/pages/CreatePremiumAdPage.tsx
import React, {useState, useMemo } from "react";
import styled from "styled-components";
import {useNavigate } from "react-router-dom";

const Container = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  background: ${({theme }) => theme.colors.white};
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  font-family: 'Inter', sans-serif;
`;

const Title = styled.h1`
  margin-bottom: 2rem;
  color: ${({theme }) => theme.colors.primaryDark};
  font-size: 1.8rem;
  font-weight: 700;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: ${({theme }) => theme.colors.dark};
  font-size: 0.95rem;
`;

const RequiredIndicator = styled.span`
  color: ${({theme }) => theme.colors.error};
  margin-left: 4px;
`;

const Input = styled.input`
  padding: 0.85rem;
  border: 1px solid ${({theme }) => theme.colors.border};
  border-radius: 6px;
  font-size: 0.95rem;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: ${({theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({theme }) => theme.colors.primary};
    outline: none;
  }
`;

const TextArea = styled.textarea`
  padding: 0.85rem;
  border: 1px solid ${({theme }) => theme.colors.border};
  border-radius: 6px;
  resize: vertical;
  min-height: 120px;
  font-size: 0.95rem;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: ${({theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({theme }) => theme.colors.primary};
    outline: none;
  }
`;

const Select = styled.select`
  padding: 0.85rem;
  border: 1px solid ${({theme }) => theme.colors.border};
  border-radius: 6px;
  font-size: 0.95rem;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: ${({theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({theme }) => theme.colors.primary};
    outline: none;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 6px;
  background: ${({theme }) => theme.colors.light};
  margin: 0.5rem 0;
`;

const CheckboxInput = styled.input`
  width: 1.2rem;
  height: 1.2rem;
  accent-color: ${({theme }) => theme.colors.primary};
  cursor: pointer;
`;

const PriceSummary = styled.div`
  padding: 1.5rem;
  background: ${({theme }) => theme.colors.light};
  border-radius: 8px;
  margin-top: 1rem;
  border: 1px solid ${({theme }) => theme.colors.borderLight};
`;

const PriceItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
`;

const PriceTotal = styled(PriceItem)`
  font-weight: 700;
  font-size: 1.1rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${({theme }) => theme.colors.border};
`;

const SubmitButton = styled.button`
  padding: 1rem;
  background: ${({theme }) => theme.colors.primary};
  color: white;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 1rem;
  font-size: 1rem;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${({theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    background: ${({theme }) => theme.colors.textLight};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ImagePreview = styled.div<{previewUrl?: string }>`
  width: 100%;
  height: 200px;
  border-radius: 6px;
  background-color: ${({theme }) => theme.colors.light};
  background-image: ${({previewUrl }) => previewUrl ? `url(${previewUrl})` : 'none'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.5rem;
  border: 1px dashed ${({theme, previewUrl }) => previewUrl ? theme.colors.border : theme.colors.textLight};
  overflow: hidden;
`;

const PreviewText = styled.span`
  color: ${({theme }) => theme.colors.textLight};
  font-size: 0.9rem;
`;

const ErrorText = styled.span`
  color: ${({theme }) => theme.colors.error};
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

export const CreatePremiumAdPage: React.FC = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [duration, setDuration] = useState("7");
  const [featured, setFeatured] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const basePricePerDay = 5; // $5 per day
  const featuredPremium = 10; // $10 for featured

  const priceBreakdown = useMemo(() => {
    const days = parseInt(duration);
    const basePrice = days * basePricePerDay;
    const featuredPrice = featured ? featuredPremium : 0;
    return {
      basePrice,
      featuredPrice,
      total: basePrice + featuredPrice
    };
  }, [duration, featured]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.match('image.*')) {
        setErrors({...errors, image: 'Please upload an image file'});
        return;
      }
      if (file.size > 2 * 1024 * 1024) {// 2MB limit
        setErrors({...errors, image: 'Image must be less than 2MB'});
        return;
      }
      
      setImage(file);
      setErrors({...errors, image: ''});
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!title.trim()) {
      newErrors.title = 'Ad title is required';
    } else if (title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
    }
    
    if (!description.trim()) {
      newErrors.description = 'Description is required';
    } else if (description.length < 20) {
      newErrors.description = 'Description should be at least 20 characters';
    }
    
    if (!image) {
      newErrors.image = 'Image is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      alert(`Ad created successfully! Total: $${priceBreakdown.total}. Redirecting to payment...`);
      navigate("/payment", {state: {amount: priceBreakdown.total } });
    } catch (error) {
      alert('Failed to create ad. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Title>Create Premium Advertisement</Title>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Ad Title <RequiredIndicator>*</RequiredIndicator></Label>
          <Input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter a compelling title for your ad"
            maxLength={100}
          />
          {errors.title && <ErrorText>{errors.title}</ErrorText>}
        </FormGroup>

        <FormGroup>
          <Label>Description <RequiredIndicator>*</RequiredIndicator></Label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your product or service in detail"
            minLength={20}
            maxLength={500}
          />
          {errors.description && <ErrorText>{errors.description}</ErrorText>}
        </FormGroup>

        <FormGroup>
          <Label>Image <RequiredIndicator>*</RequiredIndicator></Label>
          <Input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
          <ImagePreview previewUrl={imagePreview || undefined}>
            {!imagePreview && <PreviewText>Image preview will appear here</PreviewText>}
          </ImagePreview>
          {errors.image && <ErrorText>{errors.image}</ErrorText>}
        </FormGroup>

        <FormGroup>
          <Label>Promotion Duration</Label>
          <Select 
            value={duration} 
            onChange={(e) => setDuration(e.target.value)}
          >
            <option value="7">7 days ($35)</option>
            <option value="14">14 days ($70)</option>
            <option value="30">30 days ($150)</option>
          </Select>
        </FormGroup>

        <div>
          <Label>Premium Options</Label>
          <CheckboxContainer>
            <CheckboxInput
              type="checkbox"
              checked={featured}
              onChange={() => setFeatured(!featured)}
              id="featured-checkbox"
            />
            <Label htmlFor="featured-checkbox">Feature this ad at the top of listings (+$10)</Label>
          </CheckboxContainer>
        </div>

        <PriceSummary>
          <PriceItem>
            <span>Base Price ({duration} days):</span>
            <span>${priceBreakdown.basePrice}</span>
          </PriceItem>
          {featured && (
            <PriceItem>
              <span>Featured Upgrade:</span>
              <span>+${priceBreakdown.featuredPrice}</span>
            </PriceItem>
          )}
          <PriceTotal>
            <span>Total Price:</span>
            <span>${priceBreakdown.total}</span>
          </PriceTotal>
        </PriceSummary>

        <SubmitButton 
          type="submit" 
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Processing...' : 'Proceed to Secure Payment'}
        </SubmitButton>
      </Form>
    </Container>
  );
};